import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Typography, 
  Button, 
  Space, 
  Row, 
  Col, 
  Input, 
  message,
  Progress,
  Timeline,
  Avatar,
  Divider
} from 'antd';
import { 
  RobotOutlined, 
  MessageOutlined, 
  RocketOutlined, 
  ThunderboltOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  MailOutlined,
  CheckCircleOutlined,
  BulbOutlined,
  CodeOutlined,
  CloudServerOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import './BeyondAIPage.css'; // We'll create this CSS file

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const BeyondAIPage = () => {
  const [email, setEmail] = useState('');
  const [suggestion, setSuggestion] = useState('');
  const [subscribers, setSubscribers] = useState(1247);
  const [daysLeft, setDaysLeft] = useState(45);

  // Simulate increasing subscribers
  useEffect(() => {
    const interval = setInterval(() => {
      setSubscribers(prev => {
        // Random small increment every 30 seconds
        const increment = Math.floor(Math.random() * 3) + 1;
        return prev + increment;
      });
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  // Calculate days until launch (hardcoded for example)
  useEffect(() => {
    const launchDate = new Date('2024-12-25');
    const today = new Date();
    const diffTime = launchDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    setDaysLeft(diffDays > 0 ? diffDays : 0);
  }, []);

  const handleSubscribe = () => {
    if (!email || !email.includes('@')) {
      message.error('Please enter a valid email address');
      return;
    }

    // Simulate API call
    setTimeout(() => {
      message.success(`Thank you! You'll be notified when Beyond AI launches.`);
      setEmail('');
      setSubscribers(prev => prev + 1);
    }, 500);
  };

  const handleSubmitSuggestion = () => {
    if (!suggestion.trim()) {
      message.error('Please enter your suggestion');
      return;
    }

    // Simulate API call
    setTimeout(() => {
      message.success('Thank you for your suggestion! We value your input.');
      setSuggestion('');
    }, 500);
  };

  const features = [
    {
      icon: <MessageOutlined />,
      title: '24/7 Intelligent Support',
      description: 'Get instant answers to your questions anytime, anywhere'
    },
    {
      icon: <TeamOutlined />,
      title: 'Multi-Language Support',
      description: 'Communicate in your preferred language'
    },
    {
      icon: <BulbOutlined />,
      title: 'Context-Aware Responses',
      description: 'Understands context and provides relevant solutions'
    },
  ];

  const timelineItems = [
    {
      time: 'Phase 1',
      title: 'Research & Planning',
      status: 'completed',
      description: 'Market research and architecture design'
    },
    {
      time: 'Phase 2',
      title: 'Core Development',
      status: 'in-progress',
      description: 'Building the AI engine and chat interface'
    },
    {
      time: 'Phase 3',
      title: 'Beta Testing',
      status: 'pending',
      description: 'Limited release for feedback collection'
    },
    {
      time: 'Phase 4',
      title: 'Full Launch',
      status: 'pending',
      description: 'Public release and deployment'
    }
  ];

  return (
    <div className="beyond-ai-container">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="ai-icon-wrapper">
            <RobotOutlined className="ai-icon" />
          </div>
          
          <Title level={1} className=".hero-title-ai">
            Beyond AI <span className="gradient-text">Assistance</span>
          </Title>
          
          <Title level={3} className="hero-subtitle-ai">
            Revolutionizing Digital Assistance
          </Title>
          
          <Paragraph className="hero-description">
            Introducing an intelligent AI assistant that understands, learns, and helps 
            you navigate through complex studies with human-like conversations. 
          </Paragraph>

          <div className="progress-section">
            <div className="progress-info">
              <Text strong>Development Progress</Text>
              <Text type="secondary">{65}% Complete</Text>
            </div>
            <Progress 
              percent={65} 
              strokeColor={{
                '0%': '#108ee9',
                '100%': '#87d068',
              }}
              strokeWidth={8}
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <Title level={2} className="section-title">
          <ThunderboltOutlined /> What to Expect
        </Title>
        
        <Row gutter={[24, 24]} className="features-grid">
          {features.map((feature, index) => (
            <Col xs={24} sm={12} md={8} key={index}>
              <Card className="feature-card" hoverable>
                <div className="feature-icon">{feature.icon}</div>
                <Title level={4}>{feature.title}</Title>
                <Text type="secondary">{feature.description}</Text>
              </Card>
            </Col>
          ))}
        </Row>
      </section>

      {/* Timeline Section */}
      <section className="timeline-section">
        <Title level={2} className="section-title">
          <RocketOutlined /> Development Roadmap
        </Title>
        
        <Timeline 
          mode="alternate" 
          className="development-timeline"
          items={timelineItems.map((item, index) => ({
            dot: item.status === 'completed' ? 
              <CheckCircleOutlined style={{ fontSize: '16px', color: '#52c41a' }} /> :
              <ClockCircleOutlined style={{ fontSize: '16px', color: item.status === 'in-progress' ? '#1890ff' : '#d9d9d9' }} />,
            color: item.status === 'completed' ? 'green' : 
                   item.status === 'in-progress' ? 'blue' : 'gray',
            children: (
              <div className="timeline-item-content">
                <Text strong>{item.time}</Text>
                <Title level={5} style={{ margin: '8px 0' }}>{item.title}</Title>
                <Text type="secondary">{item.description}</Text>
              </div>
            )
          }))}
        />
      </section>
    </div>
  );
};

export default BeyondAIPage;